// Languages Meta can speak the "this call is recorded" announcement in
export const CALL_RECORDING_LANGUAGES = [
  { code: "en", label: "English" },
  { code: "en_IN", label: "English (India)" },
  { code: "en_US", label: "English (US)" },
  { code: "en_GB", label: "English (UK)" },
  { code: "hi", label: "Hindi" },
  { code: "kn", label: "Kannada" },
  { code: "te", label: "Telugu" },
  { code: "nl", label: "Dutch" },
  { code: "fr", label: "French" },
  { code: "de", label: "German" },
  { code: "it", label: "Italian" },
  { code: "pt", label: "Portuguese" },
  { code: "es", label: "Spanish" },
  { code: "vi", label: "Vietnamese" },
] as const;

export const CALL_DAYS = ["MONDAY", "TUESDAY", "WEDNESDAY", "THURSDAY", "FRIDAY", "SATURDAY", "SUNDAY"] as const;

export const MAX_RANGES_PER_DAY = 2;
export const MAX_HOLIDAYS = 20;
export const MAX_CALL_TEAM = 200;

export type CallHoursInput = {
  enabled: boolean;
  timezone: string;
  // Up to two open ranges per day; days left out are closed
  days: Array<{ day: (typeof CALL_DAYS)[number]; open: string; close: string }>;
  // Dates the number takes no calls between start and end (0000-2359 for the whole day)
  holidays: Array<{ date: string; start: string; end: string }>;
};

export type CallSettingsInput = {
  enabled: boolean;
  showCallButton: boolean;
  askToCallBack: boolean;
  hours: CallHoursInput;
  // purpose and language are read out for recording and transcription alike
  recording: { enabled: boolean; purpose: string; language: string };
  transcription: { enabled: boolean };
  routing: {
    // "owner": the teammate handling the customer (open ticket, lead owner, last contact) first
    ringFirst: "owner" | "everyone";
    // Who else the call rings for; null means everyone who can take calls on the number
    teamUserIds: string[] | null;
  };
  voicemail: {
    enabled: boolean;
    onDecline: boolean;
    onNoAnswer: boolean;
    // Seconds of ringing before voicemail picks up an unanswered call
    timeoutSeconds: number;
    // Meta media id of the greeting played before the beep
    announcementMediaId: string | null;
  };
};

const HHMM = /^([01]\d|2[0-3])[0-5]\d$/;

export function parseCallSettingsInput(value: unknown): { settings: CallSettingsInput } | { error: string } {
  if (!value || typeof value !== "object") return { error: "Invalid call settings" };
  const body = value as Record<string, unknown>;
  const hours = body.hours as Record<string, unknown> | undefined;
  const recording = body.recording as Record<string, unknown> | undefined;
  const voicemail = body.voicemail as Record<string, unknown> | undefined;
  const transcription = body.transcription as Record<string, unknown> | undefined;
  const routing = body.routing as Record<string, unknown> | undefined;
  if (typeof body.enabled !== "boolean" || typeof body.showCallButton !== "boolean" ||
      typeof body.askToCallBack !== "boolean" || !hours || !recording ||
      typeof hours.enabled !== "boolean" || typeof hours.timezone !== "string" || !Array.isArray(hours.days) ||
      !Array.isArray(hours.holidays) ||
      typeof recording.enabled !== "boolean" || typeof recording.purpose !== "string" ||
      typeof recording.language !== "string" || !voicemail ||
      !transcription || typeof transcription.enabled !== "boolean" ||
      !routing || (routing.ringFirst !== "owner" && routing.ringFirst !== "everyone") ||
      (routing.teamUserIds !== null && (!Array.isArray(routing.teamUserIds) ||
        routing.teamUserIds.some((id) => typeof id !== "string"))) ||
      typeof voicemail.enabled !== "boolean" || typeof voicemail.onDecline !== "boolean" ||
      typeof voicemail.onNoAnswer !== "boolean" || typeof voicemail.timeoutSeconds !== "number" ||
      (voicemail.announcementMediaId !== null && typeof voicemail.announcementMediaId !== "string")) {
    return { error: "Invalid call settings" };
  }

  const days: CallHoursInput["days"] = [];
  for (const entry of hours.days as unknown[]) {
    const day = entry as Record<string, unknown>;
    if (!CALL_DAYS.includes(day?.day as (typeof CALL_DAYS)[number]) ||
        typeof day.open !== "string" || typeof day.close !== "string" ||
        !HHMM.test(day.open) || !HHMM.test(day.close)) {
      return { error: "Call hours must use 24-hour HHMM times" };
    }
    if (day.open >= day.close) {
      const name = String(day.day).charAt(0) + String(day.day).slice(1).toLowerCase();
      return { error: `${name}: the opening time must be before the closing time` };
    }
    const range = { day: day.day as (typeof CALL_DAYS)[number], open: day.open, close: day.close };
    const sameDay = days.filter((existing) => existing.day === range.day);
    if (sameDay.length >= MAX_RANGES_PER_DAY) return { error: `Each day can have at most ${MAX_RANGES_PER_DAY} ranges` };
    if (sameDay.some((existing) => range.open < existing.close && existing.open < range.close)) {
      return { error: `${String(range.day).charAt(0)}${String(range.day).slice(1).toLowerCase()}: the two ranges can't overlap` };
    }
    days.push(range);
  }
  days.sort((a, b) => CALL_DAYS.indexOf(a.day) - CALL_DAYS.indexOf(b.day) || a.open.localeCompare(b.open));

  const holidays: CallHoursInput["holidays"] = [];
  for (const entry of hours.holidays as unknown[]) {
    const holiday = entry as Record<string, unknown>;
    if (typeof holiday?.date !== "string" || !/^\d{4}-\d{2}-\d{2}$/.test(holiday.date) ||
        typeof holiday.start !== "string" || typeof holiday.end !== "string" ||
        !HHMM.test(holiday.start) || !HHMM.test(holiday.end) || holiday.start > holiday.end) {
      return { error: "Each holiday needs a date and a valid time range" };
    }
    if (holidays.some((existing) => existing.date === holiday.date)) return { error: "Each holiday date can be listed once" };
    holidays.push({ date: holiday.date, start: holiday.start, end: holiday.end });
  }
  if (holidays.length > MAX_HOLIDAYS) return { error: `At most ${MAX_HOLIDAYS} holidays` };
  holidays.sort((a, b) => a.date.localeCompare(b.date));
  if (hours.enabled && (!hours.timezone.trim() || days.length === 0)) {
    return { error: "Call hours need a timezone and at least one open day" };
  }

  const purpose = recording.purpose.trim();
  if ((recording.enabled || transcription.enabled) && !purpose) {
    return { error: "Say why calls are recorded or transcribed; WhatsApp reads it to both sides" };
  }
  if (purpose.length > 250) return { error: "The recording purpose can be at most 250 characters" };
  if (!CALL_RECORDING_LANGUAGES.some((language) => language.code === recording.language)) {
    return { error: "Unsupported announcement language" };
  }

  const timeoutSeconds = Math.round(voicemail.timeoutSeconds);
  if (timeoutSeconds < 0 || timeoutSeconds > 30) return { error: "Voicemail can wait 0 to 30 seconds" };
  if (voicemail.enabled && !voicemail.announcementMediaId) return { error: "Upload a voicemail greeting first" };
  const teamUserIds = routing.teamUserIds === null ? null : Array.from(new Set(routing.teamUserIds as string[]));
  if (teamUserIds && teamUserIds.length > MAX_CALL_TEAM) return { error: `A call team can have at most ${MAX_CALL_TEAM} teammates` };
  if (voicemail.enabled && !voicemail.onDecline && !voicemail.onNoAnswer) {
    return { error: "Choose when voicemail answers: declined calls, unanswered calls, or both" };
  }

  return {
    settings: {
      enabled: body.enabled,
      showCallButton: body.showCallButton,
      askToCallBack: body.askToCallBack,
      hours: { enabled: hours.enabled, timezone: hours.timezone.trim(), days, holidays },
      recording: { enabled: recording.enabled, purpose, language: recording.language },
      transcription: { enabled: transcription.enabled as boolean },
      routing: { ringFirst: routing.ringFirst as "owner" | "everyone", teamUserIds },
      voicemail: {
        enabled: voicemail.enabled,
        onDecline: voicemail.onDecline,
        onNoAnswer: voicemail.onNoAnswer,
        timeoutSeconds,
        announcementMediaId: (voicemail.announcementMediaId as string | null) || null,
      },
    },
  };
}

/**
 * The one way a call is described everywhere (chat list, thread, call log, toasts), e.g. "Missed call" or
 * "Outgoing call · no answer". "Missed" only ever means a customer's call we didn't pick up.
 */
export function callPreviewText(call: { direction: string; status: string }) {
  const inbound = call.direction === "inbound";
  switch (call.status) {
    case "missed": return inbound ? "Missed call" : "Outgoing call · no answer";
    case "declined": return inbound ? "Declined call" : "Outgoing call · declined";
    case "voicemail": return "Voicemail";
    case "failed": return inbound ? "Incoming call · failed" : "Outgoing call · failed";
    case "ringing": return inbound ? "Incoming call…" : "Calling…";
    case "connected": return "On a call";
    default: return inbound ? "Incoming call" : "Outgoing call";
  }
}

/** A call transcript as Meta returns it, trimmed to what we show and give the AI. */
export type CallTranscript = {
  // ISO 639 code Meta detected, e.g. "en" or "hi"
  language: string | null;
  durationSeconds: number | null;
  segments: Array<{ speaker: "business" | "customer"; start: number; end: number; text: string }>;
};

/** "1:05" for 65 seconds into a call. */
export function formatCallOffset(seconds: number) {
  const whole = Math.max(0, Math.floor(seconds));
  const hours = Math.floor(whole / 3600);
  const minutes = Math.floor((whole % 3600) / 60);
  const rest = String(whole % 60).padStart(2, "0");
  return hours > 0 ? `${hours}:${String(minutes).padStart(2, "0")}:${rest}` : `${minutes}:${rest}`;
}
